// Settings for Sentinel access, auto-routing, delivery, and generation limits. Everything here
// follows the brand selector. The ticket cap here applies to every Sentinel in the brand; which
// brands each person works, and a cap of their own that wins over it, are set on the Users page.

import { useEffect, useMemo, useRef, useState, type AnimationEvent } from 'react';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { SEND_GATE_FOCUS, SETTINGS_FOCUS_PARAM } from '@/constants/paths';
import { SEND_GATE_QUERY_ROOT } from '@/hooks/use-deviations';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/sonner';
import { usePermissions } from '@/hooks/use-permissions';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { canManageSentinelMembersAtom, useSentinelUserBrands } from '@/hooks/use-sentinel-routing';
import { capIsValid, DEFAULT_MAX_ACTIVE_TICKETS, MAX_ACTIVE_TICKETS_CEILING } from '@/modules/sentinel-workspace/brand-team';
import { sentinelWorkspaceService } from '@/services/sentinelWorkspace.service';
import { invalidateQueries } from '@/lib/query-client';
import {
	INACTIVITY_RELEASE_OPTIONS,
	SEND_RESPONSES_TO_OPTIONS,
	type SendGateConfigDto,
	type SentinelSettingsDto,
} from '@/modules/sentinel-workspace/types';
import { FollowupSettingsSection } from '@/modules/sentinel-workspace/followups/followup-settings';
import { SettingsCard, SettingsDivider, ToggleRow } from '@/modules/settings';
import { ThresholdSlider } from './reply-thresholds';

const RESPONSE_REFINE_OPTIONS = [1, 2, 3, 5, 10, 15, 20];
const MAX_GATE_SCORE = 5;

const gateThresholdValid = (value: number): boolean => Number.isFinite(value) && value >= 0 && value <= MAX_GATE_SCORE;

/** Keyframes in index.css: an indigo ring and tint, on then off. Twice, after the scroll has had time to land. */
const SEND_GATE_BLINK_ANIMATION = 'settings-card-blink';
const SEND_GATE_BLINK_CLASS = 'animate-[settings-card-blink_500ms_ease-in-out_500ms_2]';

function SectionLabel({ children }: { children: React.ReactNode }) {
	return <p className='text-[12px] font-medium leading-4 text-gray-700 tracking-wide'>{children}</p>;
}

function FieldHelper({ children }: { children: React.ReactNode }) {
	return <p className='text-[11px] leading-4 text-gray-800'>{children}</p>;
}

export default function SentinelWorkspaceSettingsPage() {
	const { can } = usePermissions();
	const canEdit = can('edit', 'sentinel-workspace');
	// Routing is administrators only: a Sentinel holds sentinel-workspace.edit too.
	const canRoute = useAtomValue(canManageSentinelMembersAtom);
	const { businessUnits } = useBusinessUnits();

	const selectedBusinessUnitId = useAtomValue(selectedBusinessUnitIdAtom);
	const buScope = useMemo(
		() => (selectedBusinessUnitId ? { bu_id: selectedBusinessUnitId } : {}),
		[selectedBusinessUnitId],
	);

	const [settings, setSettings] = useState<SentinelSettingsDto | null>(null);
	const [saved, setSaved] = useState<SentinelSettingsDto | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	// The pre-send quality gate has its own endpoint; it saves with the rest of the page.
	const [sendGate, setSendGate] = useState<SendGateConfigDto | null>(null);
	const [savedSendGate, setSavedSendGate] = useState<SendGateConfigDto | null>(null);

	// Reloaded on every brand change, not just on mount. The page shows what is IN FORCE
	// for the selected brand — its own settings, or the account's when it has none — so a
	// stale form here is an operator editing one brand's values believing they are another's.
	const currentScope = useRef(buScope);
	useEffect(() => {
		currentScope.current = buScope;
		let cancelled = false;
		setSettings(null);
		setSaved(null);
		setSendGate(null);
		setSavedSendGate(null);
		void sentinelWorkspaceService
			.getSettings(buScope)
			.then((dto) => {
				if (cancelled) return;
				setSettings(dto);
				setSaved(dto);
			})
			.catch(() => {
				if (!cancelled) toast.error('Unable to load Sentinel settings. Please try again.');
			});
		void sentinelWorkspaceService
			.getSendGate(buScope)
			.then((dto) => {
				if (cancelled) return;
				setSendGate(dto);
				setSavedSendGate(dto);
			})
			.catch(() => {
				if (!cancelled) toast.error('Unable to load the pre-send quality gate. Please try again.');
			});
		return () => {
			cancelled = true;
		};
	}, [buScope]);

	// A link (Logs › Deviations) can ask for the gate card with ?focus=pre-send-gate. Once the card has
	// loaded it is scrolled to the middle of the screen, its switch takes focus and it blinks twice. The
	// param is then dropped, so a refresh does not do it again.
	const [searchParams, setSearchParams] = useSearchParams();
	const [blinkSendGate, setBlinkSendGate] = useState(() => searchParams.get(SETTINGS_FOCUS_PARAM) === SEND_GATE_FOCUS);
	const sendGateSectionRef = useRef<HTMLDivElement>(null);
	const sendGateShown = settings !== null && sendGate !== null;
	useEffect(() => {
		const section = sendGateSectionRef.current;
		if (!sendGateShown || !section || searchParams.get(SETTINGS_FOCUS_PARAM) !== SEND_GATE_FOCUS) return;
		const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		section.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
		section.querySelector<HTMLElement>('[role="switch"]')?.focus({ preventScroll: true });
		setSearchParams(
			(params) => {
				params.delete(SETTINGS_FOCUS_PARAM);
				return params;
			},
			{ replace: true }
		);
	}, [sendGateShown, searchParams, setSearchParams]);
	const stopSendGateBlink = (event: AnimationEvent<HTMLDivElement>) => {
		if (event.animationName === SEND_GATE_BLINK_ANIMATION) setBlinkSendGate(false);
	};

	const sendGateDirty =
		sendGate !== null && savedSendGate !== null && JSON.stringify(sendGate) !== JSON.stringify(savedSendGate);
	const settingsDirty = settings !== null && saved !== null && JSON.stringify(settings) !== JSON.stringify(saved);
	const isDirty = settingsDirty || sendGateDirty;
	const blueprintCapInvalid = settings !== null && (!Number.isInteger(settings.maxBlueprintRefines) || settings.maxBlueprintRefines < 0);
	const brandCapInvalid = settings !== null && !capIsValid(settings.defaultMaxActiveTickets);
	const sendGateInvalid = sendGate !== null && !gateThresholdValid(sendGate.blockBelow);
	const canSave = isDirty && canEdit && !isSaving && !blueprintCapInvalid && !brandCapInvalid && !sendGateInvalid;

	// Sentinels in the selected brand with a cap of their own, which the brand cap does not reach.
	const userBrands = useSentinelUserBrands();
	const ownCapCount = selectedBusinessUnitId
		? Object.values(userBrands).filter((brands) =>
				brands.some((brand) => String(brand.buId) === selectedBusinessUnitId && brand.maxActiveTickets !== null),
			).length
		: 0;
	const [isResettingCaps, setIsResettingCaps] = useState(false);

	const handleResetOwnCaps = async () => {
		if (!selectedBusinessUnitId) return;
		setIsResettingCaps(true);
		try {
			const cleared = await sentinelWorkspaceService.clearOwnCaps({ bu_id: selectedBusinessUnitId });
			void invalidateQueries(['sentinel', 'user-brands']);
			toast.success(`${cleared} Sentinel${cleared === 1 ? '' : 's'} now follow the brand cap`);
		} catch {
			toast.error('Failed to reset caps. Please try again.');
		} finally {
			setIsResettingCaps(false);
		}
	};

	const brandName = selectedBusinessUnitId
		? (businessUnits.find((unit) => unit.id === selectedBusinessUnitId)?.name ?? 'this brand')
		: null;

	const patch = (partial: Partial<SentinelSettingsDto>) =>
		setSettings((prev) => (prev ? { ...prev, ...partial } : prev));

	const patchSendGate = (partial: Partial<SendGateConfigDto>) =>
		setSendGate((prev) => (prev ? { ...prev, ...partial } : prev));

	const handleSave = async () => {
		if (!settings) return;
		setIsSaving(true);
		try {
			// The routing switch has its own endpoint (administrators only); everything else is one
			// settings write. Only what changed is sent.
			const routingChanged = canRoute && saved !== null && settings.routingEnabled !== saved.routingEnabled;
			const otherChanged =
				saved !== null && JSON.stringify({ ...settings, routingEnabled: saved.routingEnabled }) !== JSON.stringify(saved);

			// Saving while a brand is selected writes THAT brand's row, so a brand that was
			// inheriting the account's settings stops inheriting from here on.
			let next = otherChanged ? await sentinelWorkspaceService.updateSettings(settings, buScope) : settings;
			// Administrators only, like routing; the send gate is the brand's own key, not a settings row.
			const nextSendGate =
				sendGateDirty && sendGate ? await sentinelWorkspaceService.updateSendGate(sendGate, buScope) : sendGate;
			if (routingChanged) {
				const routingEnabled = await sentinelWorkspaceService.setRoutingEnabled(settings.routingEnabled, buScope);
				next = { ...next, routingEnabled };
				// The workspace shows its routed sections while any brand routes.
				void invalidateQueries(['sentinel', 'routing-enabled']);
			}
			// The brand may have changed while saving.
			if (currentScope.current === buScope) {
				setSettings(next);
				setSaved(next);
				setSendGate(nextSendGate);
				setSavedSendGate(nextSendGate);
			}
			// The sidebar reads the same flag to decide whether to show the workspace.
			void invalidateQueries(['sentinel', 'settings']);
			// The Users page shows the brand cap next to each Sentinel who follows it.
			void invalidateQueries(['sentinel', 'brand-caps']);
			// Logs › Deviations says when the gate is off.
			if (sendGateDirty) void invalidateQueries(SEND_GATE_QUERY_ROOT);
			toast.success('Settings saved');
		} catch {
			toast.error('Failed to save settings. Please try again.');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<div className='flex flex-col h-full'>
			<div className='sticky top-0 z-10 backdrop-blur-[6px] bg-white/80 border-b border-gray-500 px-4 py-4 shrink-0'>
				<p className='text-[16px] font-semibold leading-6 text-text-body'>Sentinel Workspace</p>
			</div>

			<div className='flex-1 overflow-auto'>
				{settings === null ? (
					<div className='flex w-[600px] flex-col gap-3 p-4'>
						<Skeleton className='h-14 w-full rounded-2xl' />
						<Skeleton className='h-40 w-full rounded-2xl' />
						<Skeleton className='h-24 w-full rounded-2xl' />
					</div>
				) : (
					<div className='flex flex-col gap-6 p-4'>
						{/* Sentinel access */}
						<div className='flex w-[600px] flex-col gap-3'>
							<SectionLabel>Sentinel access</SectionLabel>
							<div className='flex items-center justify-between rounded-2xl border border-gray-500 bg-white px-4 py-4'>
								<p className='text-[13px] font-normal leading-[18px] text-text-body'>Sentinel dashboard</p>
								<Switch
									checked={settings.dashboardEnabled}
									disabled={!canEdit}
									onCheckedChange={(next) => patch({ dashboardEnabled: next })}
								/>
							</div>
						</div>

						{/* Auto-routing: this brand's switch, Send capacity rule and team */}
						{canRoute ? (
							<div className='flex w-[600px] flex-col gap-3'>
								<SectionLabel>Auto-routing</SectionLabel>
								<div className='flex flex-col gap-4 rounded-2xl border border-gray-500 bg-white px-4 py-4'>
									<div className='flex items-start justify-between gap-4'>
										<div className='flex flex-col gap-1'>
											<p className='text-[13px] font-normal leading-[18px] text-text-body'>
												{brandName ? `Auto-routing for ${brandName}` : 'Auto-routing (default for brands)'}
											</p>
											<FieldHelper>
												{brandName
													? 'Assign new tickets to the least busy online Sentinel.'
													: 'Applies to every brand without its own setting. Pick a brand above to set it for that brand only.'}
											</FieldHelper>
										</div>
										<Switch
											aria-label='Auto-routing'
											checked={settings.routingEnabled}
											disabled={isSaving}
											onCheckedChange={(next) => patch({ routingEnabled: next })}
										/>
									</div>

									<div className='border-t border-dashed border-gray-300' />

									<div className='flex items-start justify-between gap-4'>
										<div className='flex flex-col gap-1'>
											<p className='text-[13px] font-normal leading-[18px] text-text-body'>Send keeps capacity</p>
											<FieldHelper>
												Only Send &amp; close frees a Sentinel&apos;s slot. Send alone keeps the ticket counted.
											</FieldHelper>
										</div>
										<Switch
											aria-label='Send keeps capacity'
											checked={settings.sendHoldsCapacity}
											disabled={isSaving || !canEdit}
											onCheckedChange={(next) => patch({ sendHoldsCapacity: next })}
										/>
									</div>

									<div className='border-t border-dashed border-gray-300' />

									<div className='flex items-start justify-between gap-4'>
										<div className='flex flex-col gap-1'>
											<p className='text-[13px] font-normal leading-[18px] text-text-body'>Release tickets when offline for</p>
											<FieldHelper>
												Reassign a Sentinel&apos;s open tickets after they&apos;ve been offline this long.
											</FieldHelper>
										</div>
										<Select
											value={String(settings.inactivityReleaseMinutes)}
											onValueChange={(value) => patch({ inactivityReleaseMinutes: Number(value) })}
											disabled={isSaving || !canEdit}
										>
											<SelectTrigger aria-label='Release tickets when offline for' className='h-8 w-[140px] rounded-xl text-[12px]'>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												{INACTIVITY_RELEASE_OPTIONS.map((option) => (
													<SelectItem key={option.value} value={String(option.value)} className='text-[12px]'>
														{option.label}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>

									<div className='border-t border-dashed border-gray-300' />

									<div className='flex items-start justify-between gap-4'>
										<div className='flex flex-col gap-1'>
											<p className='text-[13px] font-normal leading-[18px] text-text-body'>Ticket cap per Sentinel</p>
											<FieldHelper>
												{brandName
													? `The most open tickets each Sentinel in ${brandName} may hold.`
													: 'The most open tickets each Sentinel may hold, in every brand without its own setting.'}
											</FieldHelper>
											{ownCapCount > 0 ? (
												<p className='flex items-center gap-1.5 text-[11px] leading-4 text-gray-800'>
													{ownCapCount} Sentinel{ownCapCount === 1 ? ' has' : 's have'} their own cap here.
													<button
														type='button'
														onClick={() => void handleResetOwnCaps()}
														disabled={isResettingCaps || isSaving || !canEdit}
														className='cursor-pointer text-text-body underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
													>
														{isResettingCaps ? 'Resetting…' : 'Use the brand cap for everyone'}
													</button>
												</p>
											) : null}
										</div>
										<div className='flex flex-col items-end gap-1'>
											<Input
												aria-label='Ticket cap per Sentinel'
												type='number'
												min={1}
												max={MAX_ACTIVE_TICKETS_CEILING}
												step={1}
												placeholder={String(DEFAULT_MAX_ACTIVE_TICKETS)}
												value={settings.defaultMaxActiveTickets ?? DEFAULT_MAX_ACTIVE_TICKETS}
												disabled={isSaving || !canEdit}
												onChange={(event) =>
													patch({
														defaultMaxActiveTickets: event.target.value === '' ? null : Number(event.target.value),
													})
												}
												className='h-8 w-[140px] rounded-xl text-[12px]'
											/>
											{brandCapInvalid ? (
												<span className='text-[11px] text-destructive'>1 to {MAX_ACTIVE_TICKETS_CEILING}</span>
											) : null}
										</div>
									</div>
								</div>
							</div>
						) : null}

						{/* Response settings */}
						<div className='flex w-[600px] flex-col gap-3'>
							<SectionLabel>Response settings</SectionLabel>
							<div className='flex flex-col gap-4 rounded-2xl border border-gray-500 bg-white px-4 py-4'>
								<div className='flex flex-col gap-1.5'>
									<p className='text-[13px] font-normal leading-[18px] text-text-body'>Send Responses to</p>
									<Select
										value={settings.sendResponsesTo}
										disabled={!canEdit}
										onValueChange={(value) => patch({ sendResponsesTo: value as SentinelSettingsDto['sendResponsesTo'] })}
									>
										<SelectTrigger className='w-full rounded-xl'>
											<SelectValue placeholder='Choose from the list' />
										</SelectTrigger>
										<SelectContent>
											{SEND_RESPONSES_TO_OPTIONS.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldHelper>Any selection made will be forwarded to them.</FieldHelper>
								</div>

								<div className='border-t border-dashed border-gray-300' />

								<div className='flex flex-col gap-1.5'>
									<p className='text-[13px] font-normal leading-[18px] text-text-body'>Maximum regeneration attempts</p>
									<Select
										value={String(settings.maxResponseRefines)}
										disabled={!canEdit}
										onValueChange={(value) => patch({ maxResponseRefines: Number(value) })}
									>
										<SelectTrigger className='w-full rounded-xl'>
											<SelectValue placeholder='Choose from the list' />
										</SelectTrigger>
										<SelectContent>
											{RESPONSE_REFINE_OPTIONS.map((count) => (
												<SelectItem key={count} value={String(count)}>
													{count}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldHelper>Choose how many times you can refine the AI generated response</FieldHelper>
								</div>
							</div>
						</div>

						{/* Blueprint settings */}
						<div className='flex w-[600px] flex-col gap-3'>
							<SectionLabel>Blueprint settings</SectionLabel>
							<div className='flex flex-col gap-4 rounded-2xl border border-gray-500 bg-white px-4 py-4'>
								<div className='flex items-start justify-between gap-4'>
									<div className='flex flex-col gap-1'>
										<p className='text-[13px] font-normal leading-[18px] text-text-body'>Blueprint</p>
										<FieldHelper>Review the execution plan before AI starts running it.</FieldHelper>
									</div>
									<Switch
										aria-label='Blueprint review gate'
										checked={settings.blueprintEnabled}
										disabled={!canEdit}
										onCheckedChange={(next) => patch({ blueprintEnabled: next })}
									/>
								</div>

								<div className='border-t border-dashed border-gray-300' />

								<div className='flex flex-col gap-1.5'>
									<p className='text-[13px] font-normal leading-[18px] text-text-body'>Maximum regeneration attempts</p>
									<Input
										type='number'
										min={0}
										step={1}
										value={settings.maxBlueprintRefines}
										disabled={!canEdit}
										onChange={(event) => {
											const value = event.target.valueAsNumber;
											if (!Number.isNaN(value)) patch({ maxBlueprintRefines: value });
										}}
										className='rounded-xl text-[13px]'
									/>
									<FieldHelper>Choose how many times you can regenerate the blueprint before finalizing it.</FieldHelper>
								</div>
							</div>
						</div>

						{/* Pre-send quality gate: checks a reply when a Sentinel clicks Send */}
						{sendGate !== null ? (
							<div ref={sendGateSectionRef} className='flex w-[600px] flex-col gap-3' onAnimationEnd={stopSendGateBlink}>
								<SectionLabel>Pre-send quality gate</SectionLabel>
								<SettingsCard className={blinkSendGate ? SEND_GATE_BLINK_CLASS : undefined}>
									<ToggleRow
										title='Quality gate'
										description={`Pause Send when a reply scores low or has a spelling, grammar or formatting issue. ${
											brandName ? `Applies to ${brandName}.` : 'Applies to every brand without its own setting.'
										}`}
										checked={sendGate.enabled}
										disabled={isSaving || !canRoute}
										onCheckedChange={(enabled) => patchSendGate({ enabled })}
									/>
									<SettingsDivider />
									<ThresholdSlider
										label='Pause Send below AI score'
										hint='The weighted score of the four factors in the Low AI Score modal, 1–5. At 0 the score never pauses Send; writing issues still do.'
										value={sendGate.blockBelow}
										min={0}
										max={MAX_GATE_SCORE}
										step={0.1}
										error={sendGateInvalid ? `From 0 to ${MAX_GATE_SCORE}` : undefined}
										disabled={isSaving || !canRoute}
										onChange={(blockBelow) => patchSendGate({ blockBelow })}
									/>
								</SettingsCard>
							</div>
						) : null}

						<FollowupSettingsSection buScope={buScope} canEdit={canEdit} />
					</div>
				)}
			</div>

			{/* Sticky footer — Cancel + Save Changes */}
			<div className='sticky bottom-0 border-t border-gray-300 bg-white px-4 py-3 flex items-center justify-end gap-2 shrink-0 z-10'>
				<button
					type='button'
					onClick={() => {
						setSettings(saved);
						setSendGate(savedSendGate);
					}}
					disabled={!isDirty || isSaving}
					className={[
						'h-[30px] px-3 rounded-[20px] border border-gray-300 bg-white text-[12px] font-medium text-text-body transition-colors',
						!isDirty || isSaving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-100'
					].join(' ')}
				>
					Cancel
				</button>
				<button
					type='button'
					onClick={() => void handleSave()}
					disabled={!canSave}
					className={[
						'h-[30px] px-3.5 rounded-[100px] bg-text-body border border-text-quote text-[12px] font-normal text-gray-50',
						!canSave ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-text-heading'
					].join(' ')}
				>
					{isSaving ? (
						<span className='flex items-center gap-2'>
							<span className='h-3 w-3 rounded-full border-2 border-current border-t-transparent animate-spin' />
							Saving…
						</span>
					) : (
						'Save Changes'
					)}
				</button>
			</div>
		</div>
	);
}
